import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Coffee,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Swap this for your real checkout link when ready
const BOOKING_LINK = 'https://buy.stripe.com/14AbJ2fq71CD4RLbfY3VC0w';

const EVENT = {
  name: 'The Bond Coffee Lab',
  price: 125,
  location: 'Bond Coffee, Marblehead, Massachusetts',
};

// The only available date for now
const AVAILABLE_DATE = {
  year: 2026,
  month: 9, // October (0-indexed)
  day: 25,
  label: 'Sunday, October 25, 2026',
  timeSlots: [
    { id: '10am', label: '10:00 AM – 12:00 PM', available: true },
  ],
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isSameDay(d1: Date, d2: Date) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function isAvailableDay(day: Date) {
  const target = new Date(
    AVAILABLE_DATE.year,
    AVAILABLE_DATE.month,
    AVAILABLE_DATE.day
  );
  return isSameDay(day, target);
}

function CalendarGrid({
  selectedDate,
  onSelectDate,
}: {
  selectedDate: Date | null;
  onSelectDate: (d: Date) => void;
}) {
  const [viewMonth, setViewMonth] = useState(AVAILABLE_DATE.month);
  const [viewYear, setViewYear] = useState(AVAILABLE_DATE.year);

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startDayOfWeek = firstOfMonth.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewYear, viewMonth, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#132947]/10 p-6 shadow-sm">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={goPrevMonth}
          className="p-2 rounded-lg hover:bg-[#132947]/5 transition-colors text-[#132947]/60"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-bold text-[#132947]">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h3>
        <button
          onClick={goNextMonth}
          className="p-2 rounded-lg hover:bg-[#132947]/5 transition-colors text-[#132947]/60"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className="text-center text-xs font-semibold text-[#132947]/40 uppercase tracking-wider py-2"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={i} className="aspect-square" />;

          const isPast = day < today;
          const available = isAvailableDay(day);
          const selected = selectedDate && isSameDay(day, selectedDate);
          const disabled = !available || isPast;

          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => onSelectDate(day)}
              className={[
                'aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-medium transition-all duration-200 relative',
                selected
                  ? 'bg-[#132947] text-white shadow-lg'
                  : available
                    ? 'bg-[#fda36b]/15 text-[#132947] hover:bg-[#fda36b]/30 border border-[#fda36b]/30'
                    : 'text-[#132947]/25 cursor-not-allowed',
              ].join(' ')}
            >
              {day.getDate()}
              {available && !selected && (
                <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#fda36b]" />
              )}
              {selected && (
                <Check className="w-3.5 h-3.5 absolute bottom-1 text-[#fda36b]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-5 flex items-center justify-center gap-5 text-xs text-[#132947]/50">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-[#fda36b]/30 border border-[#fda36b]/30" />
          Available
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-[#132947]" />
          Selected
        </div>
      </div>
    </div>
  );
}

export default function CoffeeLabBook() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Book Your Spot | The Bond Coffee Lab';
  }, []);

  const isReady = selectedDate && selectedSlot;

  const handleCheckout = () => {
    window.open(BOOKING_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />

      {/* Back link */}
      <div className="bg-white border-b border-[#132947]/8">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Link
            to="/coffee-lab"
            className="inline-flex items-center gap-2 text-[#132947]/60 hover:text-[#132947] transition-colors font-medium text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to The Bond Coffee Lab
          </Link>
        </div>
      </div>

      <section className="py-12 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-[#fda36b] text-xs font-bold uppercase tracking-[0.2em]">
              Book Your Spot
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold text-[#132947] tracking-tight">
              Pick a date and time
            </h1>
            <p className="mt-4 text-lg text-[#132947]/60">
              ${EVENT.price} per person — six seats only.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Calendar + time slots */}
            <div className="lg:col-span-3 space-y-6">
              <CalendarGrid
                selectedDate={selectedDate}
                onSelectDate={(d) => {
                  setSelectedDate(d);
                  setSelectedSlot(null);
                }}
              />

              {/* Time slots */}
              {selectedDate && (
                <div className="bg-white rounded-2xl border border-[#132947]/10 p-6 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#fda36b] mb-4">
                    Available Times
                  </h3>
                  <div className="space-y-3">
                    {AVAILABLE_DATE.timeSlots.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot.id)}
                        className={[
                          'w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200',
                          selectedSlot === slot.id
                            ? 'border-[#132947] bg-[#132947] text-white'
                            : 'border-[#132947]/10 bg-[#faf7f2] text-[#132947] hover:border-[#132947]/30',
                        ].join(' ')}
                      >
                        <span className="flex items-center gap-3">
                          <Clock className="w-5 h-5" strokeWidth={1.5} />
                          <span className="font-semibold">{slot.label}</span>
                        </span>
                        {selectedSlot === slot.id && (
                          <Check className="w-5 h-5 text-[#fda36b]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Summary sidebar */}
            <div className="lg:col-span-2">
              <div className="bg-[#132947] rounded-2xl p-8 sticky top-8">
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#fda36b] mb-6">
                  Booking Summary
                </h3>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Coffee className="w-5 h-5 text-[#fda36b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider font-medium">
                        Event
                      </div>
                      <div className="text-white font-semibold">{EVENT.name}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#fda36b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider font-medium">
                        Date
                      </div>
                      <div className="text-white font-semibold">
                        {selectedDate
                          ? AVAILABLE_DATE.label
                          : 'Select a date'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#fda36b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider font-medium">
                        Time
                      </div>
                      <div className="text-white font-semibold">
                        {selectedSlot
                          ? AVAILABLE_DATE.timeSlots.find((s) => s.id === selectedSlot)?.label
                          : 'Select a time'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#fda36b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider font-medium">
                        Location
                      </div>
                      <div className="text-white font-semibold text-sm">{EVENT.location}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-[#fda36b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider font-medium">
                        Capacity
                      </div>
                      <div className="text-white font-semibold">6 spots only</div>
                    </div>
                  </div>
                </div>

                {/* Price + CTA */}
                <div className="border-t border-white/10 pt-6">
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="text-white/60 text-sm">Per person</span>
                    <span className="text-3xl font-bold text-white">${EVENT.price}</span>
                  </div>

                  {isReady ? (
                    <button
                      onClick={handleCheckout}
                      className="group w-full inline-flex items-center justify-center gap-3 rounded-full px-8 py-5 text-lg font-bold transition-all duration-300 transform hover:scale-[1.02] bg-[#fda36b] text-[#132947] hover:bg-[#fc9559] shadow-xl shadow-[#fda36b]/30"
                    >
                      Complete Booking
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <div className="w-full inline-flex items-center justify-center gap-3 rounded-full px-8 py-5 text-lg font-bold bg-white/10 text-white/40 cursor-not-allowed">
                      Select date & time
                    </div>
                  )}

                  <p className="mt-4 text-xs text-white/40 text-center">
                    Includes custom 12 oz coffee, Bond mug, and Bond winter hat.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
